import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DiceBox from '@3d-dice/dice-box';
import './DiceRollTumble.css';
import { getAudioService } from '../../services/audio/AudioService';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';
import { DICE_PALETTES } from './dicePalettes';
import { DICE_THEMES } from './diceThemes';
import {
  QR_NAMES,
  computeQualityRating,
  computeQualityRatingBrackets,
} from '../../engine/resolution';

/**
 * Atmosphere A2 — D100 Tumble Animation + SFX.
 *
 * 2026-06-05 dice-feedback round 8: switched the dice rendering from
 * hand-rolled three.js + cannon-es geometry (which the user correctly
 * called "retarded dwarf carvings" — primitive untextured polyhedra)
 * to the battle-tested @3d-dice/dice-box library. dice-box is a
 * BabylonJS + AmmoJS implementation used by digital tabletop apps;
 * supports d4/d6/d8/d10/d12/d20/d100 with proper textures, rounded
 * edges, plastic-die look, and real physics.
 *
 * Default notation = "2d10" (percentile pair: tens d10 with 00-90 +
 * ones d10 with 0-9). Pass `notation` to render a different roll
 * (e.g. "1d20" for a save check, "3d6" for damage).
 *
 * Mode-aware (Slice B 2026-06-03):
 *   - `selector`: 2.2s tumble window, volume read from atmosphere store
 *     (default 0.3), no transcript bubble.
 *   - `voice`:    2.5s tumble window, +0.1 SFX boost, transcript bubble
 *     shows the player's voice intent.
 *
 * dice-box runs its own physics loop and fires onRollComplete when the
 * dice settle. We use BOTH: the setTimeout-driven onComplete for the
 * reveal-beat timing the parent expects, AND dice-box's onRollComplete
 * for the actual face values (logged for future snap-to-face).
 *
 * Audio playback path is unchanged (AudioService.playDiceTumbleSfx).
 * Audio failures + DiceBox init failures are silent — atmosphere never
 * crashes the runner.
 */

export type DiceRollTumbleMode = 'selector' | 'voice';

/**
 * Roll mode = WHAT kind of roll the parent is asking for. Each mode maps to
 *   - a dice spec (notation → dice-box `roll()` call shape)
 *   - a result-chip format (percentile shows "X%", damage shows "X = a+b+c", etc.)
 *   - a color logic (percentile = tens+ones two-color split; others = single color)
 *
 * `percentile` is the default for the existing "Roll d100" button. Future
 * roll triggers (save buttons, damage rolls, voice-driven rolls,
 * paragraph-metadata-driven rolls) pass their own mode + notation.
 */
export type DiceRollMode = 'percentile' | 'skill_check' | 'save' | 'damage';

/** Standard percentile-roll notation (2 d10 — tens + ones). Kept as an
 *  exported constant for round-8/9 tests that asserted this default. */
export const DICE_DEFAULT_NOTATION = '2d10';

/** Default dice notation per roll mode. */
const DEFAULT_NOTATION_BY_MODE: Record<DiceRollMode, string> = {
  percentile: DICE_DEFAULT_NOTATION,
  skill_check: '1d100',
  save: '1d20',
  damage: '2d6',
};

interface DiceBoxResult {
  sides: number;
  value: number;
}

/** Result snapshot, computed in onRollComplete per the active mode. */
interface DiceRollResult {
  mode: DiceRollMode;
  /** Sum (or digit-concat) of dice values. For percentile this is 1-100;
   *  for damage it's the total; for save / skill check it's the raw face. */
  total: number;
  /** Per-die face values in the order they settled. Used for damage
   *  breakdown display (e.g. 14 = 5+4+5). */
  breakdown: number[];
}

export interface DiceRollTumbleProps {
  /** Fired once the tumble window has fully elapsed. */
  onComplete: () => void;
  /** Trigger path. Selector = button click; voice = dispatcher tool call. */
  mode?: DiceRollTumbleMode;
  /** Voice intent text rendered in the transcript bubble (voice mode only). */
  voiceIntent?: string | null;
  /** Tumble duration in ms. If omitted, derived from `mode`. */
  durationMs?: number;
  /** SFX volume 0.0–1.0. If omitted, derived from `mode`. */
  sfxVolume?: number;
  /** Hook for tests / parent overrides (defaults to live AudioService). */
  audioService?: { playDiceTumbleSfx(opts?: { volume?: number; durationMs?: number }): Promise<void> };
  /** Optional testid prefix; default 'br-dice-roll-tumble'. */
  testidPrefix?: string;
  /** Roll mode — controls dice spec, color logic, and chip format. Default 'percentile'. */
  rollMode?: DiceRollMode;
  /** Dice notation override. If omitted, derived from `rollMode`
   *  (percentile=2d10, skill_check=1d100, save=1d20, damage=2d6). */
  notation?: string;
  /** Optional theme override. Defaults to dice-box default theme. */
  theme?: string;
  // Round 14 (2026-06-06, Sprint 2 §4.4): fired SYNCHRONOUSLY inside the
  // dice-box onRollComplete callback (i.e., the moment the dice settle —
  // BEFORE the setTimeout-driven onComplete unmounts the tumble). Gives
  // the parent access to the result for persistence / history without
  // exposing intermediate state. Optional — existing callers that only
  // care about the unmount beat keep using onComplete alone.
  onResult?: (result: { mode: DiceRollMode; total: number; breakdown: number[] }) => void;
  // Round 14 (2026-06-06, Sprint 2 §4.3): fired when the DiceResultChip
  // element FIRST mounts (i.e., after dice settle + setResult fires).
  // Hands the chip's DOM element to the parent for bounding-box capture
  // BEFORE onComplete unmounts. Used by ScenarioRunner to launch the
  // DiceChipPortal with a static sourcePoint that survives unmount.
  onChipReady?: (el: HTMLElement) => void;
  // 2026-06-07 (UX transparency): optional interpretation for skill-check
  // rolls. When provided + rollMode === 'skill_check', the chip renders
  // a derivation breakdown below the d100 number so the player can see
  // PC × EF → SC + Pass/Fail + Quality Rating without opening the drawer.
  interpretation?: DiceInterpretation;
  // 2026-06-08 F5e fix — fires SYNCHRONOUSLY when the dice settle for a
  // skill_check roll so the parent can patch its persisted pendingRoll +
  // state.rolls entry to match the dice-box's actual outcome. Pre-fix the
  // verdict on pendingRoll came from resolveTask's internal Math.random
  // (random#1) while the chip's displayed `total` came from dice-box
  // physics (random#2) — two independent rolls glued together produced
  // visible mismatches between the rolled number, the verdict, and the
  // QR tier highlight. This callback closes that gap: the parent overwrites
  // pendingRoll.d100_roll/quality_rating/quality_name with the dice-box
  // result so persisted state matches what the player saw on screen.
  onVerdictResolved?: (verdict: {
    total: number;
    qualityRating: number | null;
    qualityName: string;
  }) => void;
  // 2026-06-08 Workstream B: Hero Point chip-button visibility.
  // When provided alongside a skill_check roll, SkillCheckInterpretation
  // renders a "Spend [H] (N)" button ON the chip so the player can spend
  // without opening the Roll Dice drawer. The button delegates entirely to
  // the existing handleSpendHeroPoint chain in ScenarioRunner — no QR-upgrade
  // logic lives here. Button hidden when onSpendHeroPoint is absent (non-HP
  // skill-check contexts, generic rolls, non-skill_check modes).
  onSpendHeroPoint?: () => void;
  /** Current Hero Point balance rendered in the button label. */
  heroPoints?: number;
  /** When false the button is disabled (HP=0 or spend in flight). */
  canSpendHeroPoint?: boolean;
}

/**
 * 2026-06-07 — derivation breakdown rendered alongside the cinematic chip.
 *
 * Surfaces the math behind a skill-check roll. Built upstream from the
 * RollLog at the moment pendingRoll is set (ScenarioRunner.tsx). When
 * mode === 'skill_check', DiceResultChip renders a small block under the
 * d100 number: "PC × EF → SC" / verdict / Quality Rating.
 *
 * Per feedback_dice_roll_outcome_not_interpreted memory anchor.
 */
export interface DiceInterpretation {
  skillName: string;
  easeFactor: string;
  primaryChance: number;
  successChance: number;
  qualityRating: number | null;
  qualityName: string; // e.g. "Outstanding" / "Failure"
}

const SELECTOR_DURATION_MS = 2200;
const SELECTOR_SFX_VOLUME = 0.3;
const VOICE_DURATION_MS = 2500;
const VOICE_SFX_VOLUME = 0.4;

// Re-exported for tests asserting defaults explicitly.
export const DICE_TUMBLE_DURATIONS = {
  selector: SELECTOR_DURATION_MS,
  voice: VOICE_DURATION_MS,
} as const;
export const DICE_TUMBLE_SFX_VOLUMES = {
  selector: SELECTOR_SFX_VOLUME,
  voice: VOICE_SFX_VOLUME,
} as const;

// Asset path served by the nginx public root (see vibe-designer/public/assets/dice-box).
const DICE_BOX_ASSET_PATH = '/assets/dice-box/';

// dice-box accepts EITHER a string notation ("1d10", "2d6") OR an object
// `{sides, qty}` per its createNotationArray dispatcher. The object form is
// what we use for the tens die — `{sides: "100", qty: 1}` (STRING "100", not
// number!) trips the equality check at dice-box.es.js:318
//   `G.sides === "100" && (G.sides = 100, G.data = "single")`
// which SETS `data: "single"` and SUPPRESSES the auto-pair macro expansion
// at world.onscreen.js:13135 (`e.sides === 100 && e.data !== "single"`).
// Result: ONE d100 mesh (00-90 face). Number 100 (instead of "100") DOES
// NOT trip the gate — the macro auto-pairs and we get 3 dice. Live-verified
// in 1.291.0 (3 dice on dallas) → 1.292.0 fixes to string form.
type DiceNotation = string | { sides: string | number; qty: number };

interface DiceBoxLike {
  init(): Promise<void>;
  roll(notation: DiceNotation, options?: Record<string, unknown>): Promise<unknown>;
  add(notation: DiceNotation, options?: Record<string, unknown>): Promise<unknown>;
  onRollComplete?: (results: unknown) => void;
  clear?(): void;
}

export function DiceRollTumble({
  onComplete,
  mode = 'selector',
  voiceIntent,
  durationMs,
  sfxVolume,
  audioService,
  testidPrefix = 'br-dice-roll-tumble',
  rollMode = 'percentile',
  notation,
  theme,
  onResult,
  onChipReady,
  interpretation,
  onVerdictResolved,
  onSpendHeroPoint,
  heroPoints,
  canSpendHeroPoint,
}: DiceRollTumbleProps) {
  const effectiveNotation = notation ?? DEFAULT_NOTATION_BY_MODE[rollMode];
  const effectiveDurationMs =
    durationMs ?? (mode === 'voice' ? VOICE_DURATION_MS : SELECTOR_DURATION_MS);
  const sfxFiredRef = useRef(false);
  const completeFiredRef = useRef(false);
  const reactId = useId();
  // DiceBox needs a CSS-selector-safe id without React's leading colon.
  const containerId = `br-dice-box-${reactId.replace(/:/g, '')}`;

  // Result populated by dice-box's onRollComplete once the dice settle.
  // Null while in flight; a DiceRollResult once visible.
  const [result, setResult] = useState<DiceRollResult | null>(null);

  const {
    diceTumbleEnabled,
    animationsEnabled,
    sfxEnabled,
    diceTumbleSfxVolume,
    dicePalette,
    diceTheme: atmosphereDiceTheme,
  } = useAtmosphereSettings();
  const palette = DICE_PALETTES[dicePalette] ?? DICE_PALETTES.bond;
  // Sprint 3 §4.2 (2026-06-06): explicit `theme` prop wins (call-site
  // override for tests + future per-roll-mode themes); otherwise fall
  // back to atmosphere store choice; otherwise the dice-box default
  // theme (systemName "default"). Resolve to a systemName string so
  // dice-box receives the canonical theme directory name.
  const effectiveTheme =
    theme ??
    (DICE_THEMES[atmosphereDiceTheme]?.systemName ?? undefined);
  const effectiveSfxVolume =
    sfxVolume ??
    Math.min(1, diceTumbleSfxVolume + (mode === 'voice' ? 0.1 : 0));
  const animationsActive = diceTumbleEnabled && animationsEnabled;
  const sfxActive = sfxEnabled;

  // --- onComplete handoff ---
  //
  // Round 15 (2026-06-06): user feedback "they are not on the screen for
  // more than a second / we need you to keep them on the screen until
  // clicked". Prior behavior fired onComplete after a fixed setTimeout
  // (2200ms selector / 2500ms voice). On cold-init the dice scene took
  // 500-1500ms to actually mount, so the effective dice-visible window
  // was under 1s. NEW behavior: dice stay rendered indefinitely after
  // settle; user dismisses by clicking the backdrop, pressing Enter,
  // or pressing Space. The animations-disabled path still fires
  // onComplete immediately (no scene to wait on).
  //
  // SFX trigger moved into the post-init() .then() chain below so SFX
  // starts at the SAME moment the dice physically begin tumbling
  // (previously SFX fired on mount before dice-box finished init).
  useEffect(() => {
    if (animationsActive) return;
    if (completeFiredRef.current) return;
    completeFiredRef.current = true;
    onComplete();
  }, [animationsActive, onComplete]);

  // Round 15 (2026-06-06): click-to-dismiss. Fires onComplete on user
  // click anywhere on the backdrop OR on Enter/Space keypress. Gated
  // on `result !== null` — dismiss is only available AFTER the dice
  // have settled and the chip is visible. Before settle, clicks pass
  // through (pointer-events: none on the stage; backdrop intercepts
  // but the handler short-circuits).
  const handleDismiss = useCallback(() => {
    if (!animationsActive) return;
    if (completeFiredRef.current) return;
    if (result === null) return;
    completeFiredRef.current = true;
    onComplete();
  }, [animationsActive, onComplete, result]);

  useEffect(() => {
    if (!animationsActive || result === null) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        handleDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [animationsActive, result, handleDismiss]);

  // 2026-06-07 (post-Casino-Royale MCP playthrough): global click dismiss.
  // The backdrop is no longer pointer-events:auto (it was blocking clicks
  // on underlying decision rows), so we listen at the window level. Any
  // click anywhere — including on a CYOA decision row, drawer button, or
  // nav — dismisses the dice modal AND naturally lets the click through
  // to its real target underneath. Gated on result !== null so clicks
  // during the active tumble don't dismiss.
  useEffect(() => {
    if (!animationsActive || result === null) return;
    const onAnyClick = () => handleDismiss();
    // Bubble phase, NOT capture: we want decision-row onClick to fire on
    // the natural target THEN this handler dismisses. Same tick, both
    // effects land — the player's "pick the matching decision" click
    // both navigates AND clears the dice cinematic.
    window.addEventListener('click', onAnyClick);
    return () => window.removeEventListener('click', onAnyClick);
  }, [animationsActive, result, handleDismiss]);

  // --- DiceBox lifecycle ---
  //
  // 2026-06-05 round 10 fix: DiceBox is created ONCE per component mount.
  // Prior rounds had palette colors + rollMode + notation in the useEffect
  // deps, which caused recreation on prop changes — racing with dice-box's
  // async `clear()` (postMessage to a shared Ammo.js worker). Result:
  // residual dice from a prior cycle leaked into the new scene (user
  // reported 3 dice on a 2-die percentile roll).
  //
  // Refs capture the latest rollMode/notation/palette at roll-time so the
  // single useEffect can still read current values without re-firing.
  const rollModeRef = useRef(rollMode);
  rollModeRef.current = rollMode;
  const notationRef = useRef(effectiveNotation);
  notationRef.current = effectiveNotation;
  const tensColorRef = useRef(palette.tensColor);
  tensColorRef.current = palette.tensColor;
  const onesColorRef = useRef(palette.onesColor);
  onesColorRef.current = palette.onesColor;
  const themeRef = useRef(effectiveTheme);
  themeRef.current = effectiveTheme;
  // Round 14 (2026-06-06, Sprint 2): hold the parent's onResult / onChipReady
  // through refs so the dice-box onRollComplete + chip-mount effects can
  // dispatch latest values without re-firing the main lifecycle useEffect
  // on prop changes (the create-once-per-mount discipline established in
  // round 10).
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const onChipReadyRef = useRef(onChipReady);
  onChipReadyRef.current = onChipReady;
  const onVerdictResolvedRef = useRef(onVerdictResolved);
  onVerdictResolvedRef.current = onVerdictResolved;
  // Round 15 (2026-06-06): SFX-related state captured through refs so the
  // post-init dice-box callback can read latest values without re-firing
  // the main lifecycle effect on prop changes. Mirrors the create-once
  // discipline established in round 10.
  const sfxActiveRef = useRef(sfxActive);
  sfxActiveRef.current = sfxActive;
  const audioServiceRef = useRef(audioService);
  audioServiceRef.current = audioService;
  const effectiveSfxVolumeRef = useRef(effectiveSfxVolume);
  effectiveSfxVolumeRef.current = effectiveSfxVolume;
  const effectiveDurationMsRef = useRef(effectiveDurationMs);
  effectiveDurationMsRef.current = effectiveDurationMs;

  useEffect(() => {
    if (!animationsActive) return;
    let box: DiceBoxLike | null = null;
    let cancelled = false;
    setResult(null);
    try {
      const options: Record<string, unknown> = {
        assetPath: DICE_BOX_ASSET_PATH,
        scale: 8,
        gravity: 2,
        mass: 1,
        friction: 0.8,
        restitution: 0.0,
        angularDamping: 0.4,
        linearDamping: 0.5,
        spinForce: 6,
        throwForce: 5,
        startingHeight: 8,
        settleTimeout: 5000,
        onRollComplete: (results: unknown) => {
          if (cancelled) return;
          // Sprint 4 §4.5 (2026-06-06): dice-box `getRollResults()` (dice-box.es.js:357)
          // returns an Array of GROUP objects, one per `roll()/add()` call:
          //   [{value: <group-sum>, qty: <N>, rolls: [{value, sides}, ...]}, ...]
          // Pre-fix we read `.value` off the group (the SUM) and called it the die
          // value — so a 2d6 roll producing [3, 4] gave breakdown=[7] instead of
          // breakdown=[3, 4]. User-reported "Dice: 7" instead of "Dice: 3, 4."
          // Symptom only visible for multi-die rolls; 1d20 saves looked fine
          // because qty=1 → group.value = single die value. The fix iterates each
          // group's `rolls` array to get per-die values.
          const flat: DiceBoxResult[] = [];
          if (Array.isArray(results)) {
            for (const entry of results) {
              if (!entry || typeof entry !== 'object') continue;
              // Group object: { value, qty, rolls: [{value, sides}, ...] }
              const rolls = (entry as { rolls?: unknown }).rolls;
              if (Array.isArray(rolls)) {
                for (const die of rolls) {
                  if (die && typeof (die as DiceBoxResult).value === 'number') {
                    flat.push(die as DiceBoxResult);
                  }
                }
              } else if (typeof (entry as DiceBoxResult).value === 'number') {
                // Defensive fallback: dice-box pre-1.0 versions sometimes pass
                // a flat list of dice without a group wrapper. Keep that path
                // alive so a future library bump doesn't silently zero-out.
                flat.push(entry as DiceBoxResult);
              }
            }
          }
          const breakdown = flat.map((r) => r.value);
          let total: number;
          if (rollModeRef.current === 'percentile' && breakdown.length >= 2) {
            // Round 12 (2026-06-05): tens is a SINGLE d100 die — dice-box
            // returns its face value DIRECTLY in {0, 10, 20, ..., 90}.
            // The ones die is a single d10 — dice-box bumps 0→10, so its
            // value is in {1, 2, ..., 10} where 10 represents the "0" face.
            //   tens=70, ones=3  → 70 + 3 = 73
            //   tens=0,  ones=10 → 0 + 0 = 0 → 100 (max-roll convention)
            //   tens=0,  ones=5  → 0 + 5 = 5
            //   tens=90, ones=10 → 90 + 0 = 90
            const tens = breakdown[0];
            const ones = breakdown[1];
            const onesDigit = ones === 10 ? 0 : ones;
            const concat = tens + onesDigit;
            total = concat === 0 ? 100 : concat;
          } else {
            // Damage / save / skill check: plain sum.
            total = breakdown.reduce((s, v) => s + v, 0);
          }
          const resolved = { mode: rollModeRef.current, total, breakdown };
          setResult(resolved);
          // Round 14 (2026-06-06, Sprint 2 §4.4): notify parent at the
          // moment the dice settle — before the setTimeout-driven
          // onComplete unmount races us out of the DOM.
          if (onResultRef.current) {
            try {
              onResultRef.current(resolved);
            } catch {
              // Parent handler failure must not break the tumble lifecycle.
            }
          }
        },
      };
      if (themeRef.current) options.theme = themeRef.current;
      const ctor = DiceBox as unknown as new (sel: string, opts: Record<string, unknown>) => DiceBoxLike;
      box = new ctor(`#${containerId}`, options);
      const initPromise = box.init();
      Promise.resolve(initPromise)
        .then(() => {
          if (cancelled || !box) return;
          // Round 15 (2026-06-06): fire SFX at the SAME moment the dice
          // physically start tumbling — user reported "dice are not
          // starting at the same time as the sound" because the prior
          // SFX useEffect fired on MOUNT, before dice-box's async init()
          // resolved. On cold-init that gap was 500-1500ms — sound was
          // already winding down before dice appeared. Moving the SFX
          // trigger into this .then() block aligns them.
          if (!sfxFiredRef.current && sfxActiveRef.current) {
            sfxFiredRef.current = true;
            const service = audioServiceRef.current ?? getAudioService();
            service
              .playDiceTumbleSfx({
                volume: effectiveSfxVolumeRef.current,
                durationMs: effectiveDurationMsRef.current - 100,
              })
              .catch(() => {
                // Audio failure is silent.
              });
          }
          // Round 12 (2026-06-05): percentile uses object-form `{sides: "100",
          // qty: 1}` — STRING "100", NOT number 100. dice-box es.js:318:
          //   G.sides === "100" && (G.sides = 100, G.data = "single")
          // The equality check is against the STRING literal. Number 100
          // does NOT satisfy `=== "100"`, so `data: "single"` stays unset
          // and the auto-pair macro at world.onscreen.js:13135 still fires
          // (produces tens d100 + auto-paired ones d10 in the SAME color).
          // 1.291.0 shipped with `sides: 100` (number) and live-verified
          // 3 dice on dallas. 1.292.0 changes to `sides: "100"` so the
          // gate trips and we get a SINGLE d100 mesh (00-90 face) — the
          // separate `add('1d10', ...)` appends ones in distinct color.
          //   Net: EXACTLY 2 dice, distinct colors, tens prints 00-90,
          //   ones prints 0-9.
          if (rollModeRef.current === 'percentile') {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            box.roll({ sides: '100', qty: 1 }, { themeColor: tensColorRef.current });
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            box.add('1d10', { themeColor: onesColorRef.current });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            box.roll(notationRef.current, { themeColor: tensColorRef.current });
          }
        })
        .catch(() => {
          // DiceBox init failure is silent — onComplete still fires via the
          // animations-disabled fallback or the click-to-dismiss handler.
        });
    } catch {
      box = null;
    }
    return () => {
      cancelled = true;
      try { box?.clear?.(); } catch { /* swallow dispose errors */ }
    };
  }, [animationsActive, containerId]);

  const showTranscriptBubble =
    mode === 'voice' && typeof voiceIntent === 'string' && voiceIntent.length > 0;

  // Reduced-motion / animations-disabled fallback render.
  if (!animationsActive) {
    return (
      <div
        className="br-dice-roll-tumble-backdrop br-dice-roll-tumble-static"
        data-testid={`${testidPrefix}-backdrop`}
        data-mode={mode}
        data-roll-mode={rollMode}
        data-notation={effectiveNotation}
        data-static="true"
        aria-live="polite"
        aria-label="Rolling dice"
      >
        <div
          className="br-dice-roll-tumble-static-pill"
          data-testid={`${testidPrefix}-static`}
        >
          rolling…
        </div>
        {showTranscriptBubble && (
          <div
            className="br-dice-roll-tumble-transcript"
            data-testid={`${testidPrefix}-transcript`}
          >
            <span className="br-dice-roll-tumble-transcript-text">
              {voiceIntent}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="br-dice-roll-tumble-backdrop"
      data-testid={`${testidPrefix}-backdrop`}
      data-mode={mode}
      // Sprint 4 §4.5 (2026-06-06): surface rollMode + notation on the
      // backdrop so the voice-force-dice-roll Playwright spec can assert
      // the correct mode routed end-to-end. The DiceResultChip already
      // has data-roll-mode on it, but the chip mounts AFTER settle —
      // the spec needs to observe the backdrop the moment it appears.
      data-roll-mode={rollMode}
      data-notation={effectiveNotation}
      data-dismiss-ready={result !== null ? 'true' : 'false'}
      aria-live="polite"
      aria-label={result !== null ? 'Rolling dice — click to continue' : 'Rolling dice'}
      role={result !== null ? 'button' : undefined}
      tabIndex={result !== null ? 0 : undefined}
      onClick={result !== null ? handleDismiss : undefined}
      // 2026-06-07: removed inline `pointerEvents: 'auto'` — the backdrop
      // was blocking clicks on underlying scenario decisions. Backdrop is
      // now pointer-events:none (CSS); dismissal happens via the chip's
      // own click, Enter/Space keys, or the window-level click handler.
    >
      <div
        id={containerId}
        className="br-dice-roll-tumble-stage"
        data-testid={testidPrefix}
        aria-hidden="true"
      />
      {result !== null && (
        <DiceResultChip
          result={result}
          notation={effectiveNotation}
          testidPrefix={testidPrefix}
          interpretation={
            rollMode === 'skill_check' ? interpretation : undefined
          }
          // Round 14 (2026-06-06, Sprint 2 §4.3): capture the chip's
          // bounding box BEFORE the tumble unmounts so ScenarioRunner
          // can launch DiceChipPortal with a static sourcePoint. Fires
          // once per chip mount (per onRollComplete in dice-box).
          onMount={(el) => {
            if (onChipReadyRef.current) {
              try {
                onChipReadyRef.current(el);
              } catch {
                // Parent handler failure must not break the chip render.
              }
            }
          }}
          // 2026-06-08 F5e fix: single-RNG invariant for skill-check rolls.
          // Fires the moment the chip mounts with the dice-box `total`;
          // SkillCheckInterpretation recomputes QR from total + SC inline,
          // and this callback informs the parent so persisted state matches.
          onVerdictResolved={(verdict) => {
            if (onVerdictResolvedRef.current) {
              try {
                onVerdictResolvedRef.current(verdict);
              } catch {
                // Parent handler failure must not break the chip render.
              }
            }
          }}
          // 2026-06-08 Workstream B: thread HP props to SkillCheckInterpretation.
          onSpendHeroPoint={rollMode === 'skill_check' ? onSpendHeroPoint : undefined}
          heroPoints={heroPoints}
          canSpendHeroPoint={canSpendHeroPoint}
        />
      )}
      {showTranscriptBubble && (
        <div
          className="br-dice-roll-tumble-transcript"
          data-testid={`${testidPrefix}-transcript`}
        >
          <span className="br-dice-roll-tumble-transcript-text">
            {voiceIntent}
          </span>
        </div>
      )}
      {result !== null && (
        <div
          className="br-dice-roll-tumble-dismiss-hint"
          data-testid={`${testidPrefix}-dismiss-hint`}
        >
          Click anywhere to continue ▸
        </div>
      )}
    </div>
  );
}

export default DiceRollTumble;

// ---------------------------------------------------------------------------
// Result chip — renders the rolled value with the right format per mode.
// ---------------------------------------------------------------------------

interface DiceResultChipProps {
  result: DiceRollResult;
  notation: string;
  testidPrefix: string;
  /** Round 14 (Sprint 2 §4.3): callback ref fired when the chip's
   *  outer div first mounts in the DOM. Used by DiceRollTumble to
   *  surface the chip element to the parent for portal launch. */
  onMount?: (el: HTMLDivElement) => void;
  /** 2026-06-07: skill-check derivation breakdown (PC × EF → SC + verdict
   *  + QR). Honored only when result.mode === 'skill_check'. */
  interpretation?: DiceInterpretation;
  /** 2026-06-08 F5e fix: fires SYNCHRONOUSLY on chip mount with the
   *  dice-box-derived (total, qr, qualityName). The parent uses this to
   *  patch its persisted pendingRoll + state.rolls so the verdict matches
   *  the visible dice. Honored only when result.mode === 'skill_check'
   *  AND interpretation is provided. */
  onVerdictResolved?: (verdict: {
    total: number;
    qualityRating: number | null;
    qualityName: string;
  }) => void;
  /** 2026-06-08 Workstream B: threaded from DiceRollTumbleProps → here →
   *  SkillCheckInterpretation. See DiceRollTumbleProps for full doc. */
  onSpendHeroPoint?: () => void;
  heroPoints?: number;
  canSpendHeroPoint?: boolean;
}

/**
 * 2026-06-08 — Skill-check interpretation block.
 *
 * Round 17 (2026-06-09): testid renamed `${prefix}-interpretation` →
 * `${prefix}-skillcheck-interpretation` to disambiguate from the
 * DamageInterpretation block (which uses `${prefix}-damage-interpretation`).
 * Pre-rename both components emitted the same testid; mode-gated so safe at
 * runtime, but a name-collision smell flagged by Round 16 G1 reviewer.
 *
 * Renders three rows inside the dice chip:
 *   1. Formula line — `{Skill} · PC X × EF Y → SC Z`
 *   2. Verdict line — `✓ Excellent (QR 1)` or `✗ Failure`
 *   3. NEW explainer — walks the player through the d100 ≤ SC contract
 *      AND lists the QR-tier brackets for the current SC (computed via
 *      `computeQualityRatingBrackets`, the same engine helper that backs
 *      the Roll Dice drawer's pre-roll preview).
 *
 * Per user feedback 2026-06-08: "there should be text explaining how the
 * roll is calculated for the human player in addition to the data you
 * are showing".
 */
interface SkillCheckInterpretationProps {
  interpretation: DiceInterpretation;
  total: number;
  testidPrefix: string;
  /** 2026-06-08 F5e fix: fires exactly once on mount with the verdict
   *  derived from `total` + `interpretation.successChance`. Lets the
   *  grandparent (ScenarioRunner) patch its persisted pendingRoll +
   *  state.rolls so the saved log matches what the player saw. */
  onVerdictResolved?: (verdict: {
    total: number;
    qualityRating: number | null;
    qualityName: string;
  }) => void;
  /** 2026-06-08 Workstream B: when present, renders "Spend [H] (N)" on
   *  the chip. Disabled when canSpendHeroPoint=false OR derivedQr===1
   *  (Critical Success — no further upgrade possible). The click delegates
   *  to the existing handleSpendHeroPoint → handleChipAnimationComplete
   *  chain in ScenarioRunner; no QR math lives here. */
  onSpendHeroPoint?: () => void;
  heroPoints?: number;
  canSpendHeroPoint?: boolean;
}

function SkillCheckInterpretation({
  interpretation,
  total,
  testidPrefix,
  onVerdictResolved,
  onSpendHeroPoint,
  heroPoints,
  canSpendHeroPoint,
}: SkillCheckInterpretationProps) {
  // 2026-06-08 F5e fix: derive verdict from the dice-box `total` (the
  // physically-simulated d100 the player just saw settle) + the SC the
  // interpretation carries. Pre-fix this read `interpretation.qualityRating`
  // which came from resolveTask's INTERNAL Math.random (random#1), while
  // `total` came from dice-box's PHYSICS RNG (random#2). The two never
  // agreed on the same d100 value, so the chip displayed one number with
  // a verdict derived from a different number, plus a QR-tier highlight
  // tracking the verdict (random#1). Recomputing here closes that gap.
  const derivedQr = useMemo(() => {
    if (!Number.isInteger(total) || total < 1 || total > 100) return null;
    try {
      return computeQualityRating(interpretation.successChance, total);
    } catch {
      return null;
    }
  }, [interpretation.successChance, total]);
  const derivedQualityName = derivedQr === null ? 'Failure' : QR_NAMES[derivedQr];
  const verdictSuccess = derivedQr !== null;
  const breakdown = useMemo(
    () => computeQualityRatingBrackets(interpretation.successChance),
    [interpretation.successChance],
  );

  // 2026-06-08 F5e: fire onVerdictResolved EXACTLY ONCE per mount so
  // ScenarioRunner can patch pendingRoll + the last state.rolls entry to
  // reflect the dice-box outcome. Same useRef + empty-deps pattern as
  // DiceResultChip's onMount — prevents the re-fire-on-every-parent-render
  // bug that Sprint 4 §4.5 (2026-06-06) called out for onChipReady.
  const onVerdictResolvedRef = useRef(onVerdictResolved);
  onVerdictResolvedRef.current = onVerdictResolved;
  useLayoutEffect(() => {
    if (onVerdictResolvedRef.current) {
      try {
        onVerdictResolvedRef.current({
          total,
          qualityRating: derivedQr,
          qualityName: derivedQualityName,
        });
      } catch {
        // Parent handler failure must not break the chip render.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      className={`br-dice-roll-tumble-interpretation br-dice-roll-tumble-interpretation--${verdictSuccess ? 'success' : 'failure'}`}
      data-testid={`${testidPrefix}-skillcheck-interpretation`}
      data-verdict={verdictSuccess ? 'success' : 'failure'}
      data-qr={derivedQr ?? 'fail'}
    >
      <div className="br-dice-roll-tumble-interpretation-formula">
        {interpretation.skillName} · PC {interpretation.primaryChance} × EF {interpretation.easeFactor} → SC {interpretation.successChance}
      </div>
      <div className="br-dice-roll-tumble-interpretation-verdict">
        {verdictSuccess
          ? `✓ ${derivedQualityName} (QR ${derivedQr})`
          : `✗ ${derivedQualityName}`}
      </div>
      <div
        className="br-dice-roll-tumble-interpretation-explainer"
        data-testid={`${testidPrefix}-skillcheck-interpretation-explainer`}
      >
        <div className="br-dice-roll-tumble-interpretation-explainer-line">
          You rolled <strong>{total}</strong> on 1d100. SC {interpretation.successChance} is your win threshold —{' '}
          {verdictSuccess ? 'roll ≤ SC succeeds.' : 'roll > SC (or exact 100) fails.'}
        </div>
        {breakdown && (
          <ul
            className="br-dice-roll-tumble-interpretation-explainer-tiers"
            data-testid={`${testidPrefix}-skillcheck-interpretation-tiers`}
          >
            {breakdown.brackets.map((b) => (
              <li
                key={b.qr}
                className={`br-dice-roll-tumble-interpretation-explainer-tier br-dice-roll-tumble-interpretation-explainer-tier--qr${b.qr}${
                  derivedQr === b.qr
                    ? ' br-dice-roll-tumble-interpretation-explainer-tier--active'
                    : ''
                }`}
              >
                <span className="br-dice-roll-tumble-interpretation-explainer-tier-label">
                  QR{b.qr} {b.qrName}
                </span>
                <span className="br-dice-roll-tumble-interpretation-explainer-tier-range">
                  roll {b.low === b.high ? b.low : `${b.low}–${b.high}`} · {b.chancePercent}%
                </span>
              </li>
            ))}
            <li
              className={`br-dice-roll-tumble-interpretation-explainer-tier br-dice-roll-tumble-interpretation-explainer-tier--fail${
                !verdictSuccess
                  ? ' br-dice-roll-tumble-interpretation-explainer-tier--active'
                  : ''
              }`}
            >
              <span className="br-dice-roll-tumble-interpretation-explainer-tier-label">
                Failure
              </span>
              <span className="br-dice-roll-tumble-interpretation-explainer-tier-range">
                roll {breakdown.failureLow === breakdown.failureHigh
                  ? breakdown.failureLow
                  : `${breakdown.failureLow}–${breakdown.failureHigh}`}{' '}
                · {breakdown.failureChancePercent}%
              </span>
            </li>
          </ul>
        )}
      </div>
      {/* 2026-06-08 Workstream B: Spend [H] button on the cinematic chip.
       *  Present only when onSpendHeroPoint is provided (i.e. the caller is
       *  ScenarioRunner with HP wired). Disabled when (a) canSpendHeroPoint
       *  is false (HP=0) OR (b) derivedQr===1 — Critical Success cannot be
       *  upgraded further. QR-upgrade math NOT duplicated here; click fires
       *  the parent's handleSpendHeroPoint which calls the existing
       *  spendHeroPoint → setChipFlying → handleChipAnimationComplete chain. */}
      {onSpendHeroPoint && (
        <button
          type="button"
          className="br-dice-roll-tumble-interpretation-spend-hp"
          data-testid={`${testidPrefix}-chip-spend-hero-point`}
          onClick={onSpendHeroPoint}
          disabled={!canSpendHeroPoint || derivedQr === 1}
          title={
            derivedQr === 1
              ? 'Critical Success — no upgrade available'
              : canSpendHeroPoint
                ? 'Spend a Hero Point to upgrade this roll'
                : 'No Hero Points remaining'
          }
        >
          Spend [H] ({heroPoints ?? 0})
        </button>
      )}
    </div>
  );
}

/**
 * 2026-06-09 (Round 16) — Damage notation parser.
 *
 * Pure function. Parses an NdM[+/-K] notation into qty/sides/modifier
 * plus computed min/max/avg for the DamageInterpretation explainer.
 * Returns null on malformed input (qty < 1, sides < 2, or regex miss)
 * so the chip can gracefully fall back to its minimal "= a+b" render.
 *
 * Math:
 *   min = qty * 1 + modifier
 *   max = qty * sides + modifier
 *   avg = qty * (sides + 1) / 2 + modifier
 *
 * Examples:
 *   parseDamageNotation('2d6')    → { qty:2, sides:6,  mod:0,  min:2, max:12, avg:7    }
 *   parseDamageNotation('3d8+2')  → { qty:3, sides:8,  mod:2,  min:5, max:26, avg:15.5 }
 *   parseDamageNotation('1d6-1')  → { qty:1, sides:6,  mod:-1, min:0, max:5,  avg:2.5  }
 *   parseDamageNotation('garbage')→ null
 *   parseDamageNotation('1d1')    → null  (sides < 2)
 *   parseDamageNotation('0d6')    → null  (qty < 1)
 */
export function parseDamageNotation(notation: string): {
  qty: number;
  sides: number;
  modifier: number;
  min: number;
  max: number;
  avg: number;
} | null {
  const m = notation.trim().match(/^(\d+)d(\d+)([+-]\d+)?$/i);
  if (!m) return null;
  const qty = parseInt(m[1], 10);
  const sides = parseInt(m[2], 10);
  const modifier = m[3] ? parseInt(m[3], 10) : 0;
  if (qty < 1 || sides < 2) return null;
  const min = qty * 1 + modifier;
  const max = qty * sides + modifier;
  const avg = (qty * (sides + 1)) / 2 + modifier;
  return { qty, sides, modifier, min, max, avg };
}

/**
 * 2026-06-09 (Round 16) — DamageInterpretation block.
 *
 * Round 17 (2026-06-09): testid renamed `${prefix}-interpretation` →
 * `${prefix}-damage-interpretation` to disambiguate from the
 * SkillCheckInterpretation block (which uses `${prefix}-skillcheck-*`).
 *
 * Renders under the damage chip's main number. Parallel to
 * SkillCheckInterpretation but lighter — no verdict (damage has no
 * canonical pass/fail), no Hero Point button, no QR tier brackets.
 *
 * Two lines:
 *   1. Formula:  "Damage roll · {notation}"
 *   2. Explainer: "You rolled {total} on {notation}. Range {min}–{max}, average {avg}."
 *
 * Closes user audit 2026-06-09: "the display card is thin content".
 * The current chip showed just `7 = 3+4` with no semantic context for
 * what the roll WAS. This block adds notation context + a deterministic
 * range/average explainer derived purely from the notation.
 *
 * Returns null on malformed notation (no rendering) so the chip falls
 * back to its existing minimal "= a+b" meta line.
 */
interface DamageInterpretationProps {
  total: number;
  notation: string;
  testidPrefix: string;
}

function DamageInterpretation({ total, notation, testidPrefix }: DamageInterpretationProps) {
  const parsed = useMemo(() => parseDamageNotation(notation), [notation]);
  if (parsed === null) return null;
  const { min, max, avg } = parsed;
  // Format avg to 1 decimal place when fractional; integer when whole.
  const avgLabel = Number.isInteger(avg) ? String(avg) : avg.toFixed(1);
  return (
    <div
      className="br-dice-roll-tumble-interpretation br-dice-roll-tumble-interpretation--damage"
      data-testid={`${testidPrefix}-damage-interpretation`}
      data-verdict="damage"
    >
      <div className="br-dice-roll-tumble-interpretation-formula">
        Damage roll · {notation}
      </div>
      <div
        className="br-dice-roll-tumble-interpretation-explainer"
        data-testid={`${testidPrefix}-damage-interpretation-explainer`}
      >
        <div className="br-dice-roll-tumble-interpretation-explainer-line">
          You rolled <strong>{total}</strong> on {notation}. Range {min}–{max}, average {avgLabel}.
        </div>
      </div>
    </div>
  );
}

function DiceResultChip({ result, notation, testidPrefix, onMount, interpretation, onVerdictResolved, onSpendHeroPoint, heroPoints, canSpendHeroPoint }: DiceResultChipProps) {
  const { mode, total, breakdown } = result;
  const ariaLabel =
    mode === 'percentile' ? `Percentile roll: ${total} percent`
    : mode === 'damage' ? `Damage roll total: ${total}. Dice: ${breakdown.join(', ')}.`
    : mode === 'save' ? `Save roll: ${total} on ${notation}`
    : /* skill_check */ `Skill check: ${total}`;
  // Sprint 4 §4.5 (2026-06-06): fire onMount exactly once when the chip
  // first appears in the DOM. The earlier `ref={(el) => ...}` inline
  // pattern created a new function every render, and React detaches+
  // re-attaches refs whenever the function identity changes — so onMount
  // fired on EVERY parent re-render. Each fire called the parent's
  // setPendingChipFly, which re-rendered ScenarioRunner, which re-rendered
  // this chip, which fired onMount again — infinite loop. User-visible
  // symptom: a continuous stream of chips flying to the History toggle.
  // The useRef+useLayoutEffect pair makes the trigger fire exactly once
  // per mount; onMountRef captures the latest callback without re-firing.
  const chipRef = useRef<HTMLDivElement | null>(null);
  const onMountRef = useRef(onMount);
  onMountRef.current = onMount;
  useLayoutEffect(() => {
    if (chipRef.current && onMountRef.current) {
      onMountRef.current(chipRef.current);
    }
  }, []);
  return (
    <div
      className={`br-dice-roll-tumble-value br-dice-roll-tumble-value--${mode}`}
      data-testid={`${testidPrefix}-value`}
      data-roll-mode={mode}
      aria-live="polite"
      aria-label={ariaLabel}
      ref={chipRef}
    >
      <span className="br-dice-roll-tumble-value-number">{total}</span>
      {mode === 'percentile' && (
        <span className="br-dice-roll-tumble-value-suffix">%</span>
      )}
      {mode === 'save' && (
        <span className="br-dice-roll-tumble-value-meta">{notation}</span>
      )}
      {mode === 'damage' && breakdown.length > 1 && (
        <span className="br-dice-roll-tumble-value-meta">
          = {breakdown.join('+')}
        </span>
      )}
      {mode === 'skill_check' && (
        <span className="br-dice-roll-tumble-value-meta">d100</span>
      )}
      {/* Round 16 (2026-06-09): damage chip thickening. Adds a 2-line
       *  derivation block (formula + range/avg explainer) so damage rolls
       *  carry semantic context instead of just floating "7 = 3+4".
       *  Closes user audit "the display card is thin content." */}
      {mode === 'damage' && (
        <DamageInterpretation
          total={total}
          notation={notation}
          testidPrefix={testidPrefix}
        />
      )}
      {/* 2026-06-07: skill-check derivation breakdown surfaced ON the chip
       *  so the player sees the math without opening the Roll Dice drawer.
       *  Per feedback_dice_roll_outcome_not_interpreted memory anchor.
       *  2026-06-08: added an explainer block below the verdict — walks the
       *  human player through HOW the result was reached + lists the QR
       *  tier brackets for the active SC so they can see why the verdict
       *  landed where it did. */}
      {mode === 'skill_check' && interpretation && (
        <SkillCheckInterpretation
          interpretation={interpretation}
          total={total}
          testidPrefix={testidPrefix}
          onVerdictResolved={onVerdictResolved}
          onSpendHeroPoint={onSpendHeroPoint}
          heroPoints={heroPoints}
          canSpendHeroPoint={canSpendHeroPoint}
        />
      )}
    </div>
  );
}
